import { useEffect, useState, useRef } from 'react';
import { GoogleMap, Marker, Polygon, OverlayView } from '@react-google-maps/api';
import axios from 'axios';
import pegman from '../assets/pegman.png';

const useGoogleMapsScript = (apiKey) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if (window.google) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps script');
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [apiKey]);
  
  return isScriptLoaded;
};

const Geofence = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const isScriptLoaded = useGoogleMapsScript(apiKey);
  const [geofences, setGeofences] = useState([]);
  const [mapType, setMapType] = useState('satellite');
  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [isDeletingPin, setIsDeletingPin] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [isAreaGenerated, setIsAreaGenerated] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [geofence_name, setgeofence_name] = useState('');
  const [prescription, setPrescription] = useState('');
  const [error, setError] = useState(null);
  const [systems, setSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null); // Stores user's location
  const [mapCenter, setMapCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  
  const mapRef = useRef();

  // Set center only when a new system is selected (only runs once per selection)
  useEffect(() => {
    if (selectedSystem) {
      setMapCenter({
        lat: parseFloat(selectedSystem.latitude),
        lng: parseFloat(selectedSystem.longitude),
      });
    }
  }, [selectedSystem]);

  // Load Systems from IoT Core
  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const token = localStorage.getItem("access");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user-systems/`,
          {
            headers: {
              Authorization: `JWT ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        console.log("Raw Response:", response);

        if (!response.ok) {
          throw new Error("Failed to fetch systems.");
        }

        const data = await response.json();
        setSystems(data.systems || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, []);

  const fetchGeofencesForSystem = async (systemId) => {
    try {
      const token = localStorage.getItem("access");
      if (!token) {
        console.error("No token found");
        return;
      }
  
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/geofences/`,
        {
          headers: {
            Authorization: `JWT ${token}`,
          },
          params: {
            system_id: systemId,
          },
        }
      );
  
      // Save the fetched geofences in state
      setGeofences(response.data.geofences); 
    } catch (error) {
      console.error(
        "Error fetching geofences:",
        error.response?.data || error.message
      );
      alert("Failed to fetch geofences. Please try again.");
    }
  };  

  // Function to calculate the center of the polygon
  const calculateCenter = (markers) => {
    if (!markers || markers.length === 0) return { lat: 0, lng: 0 };

    let latSum = 0, lngSum = 0;

    markers.forEach((marker) => {
      latSum += marker.lat;
      lngSum += marker.lng;
    });

    return {
      lat: latSum / markers.length,
      lng: lngSum / markers.length,
    };
  };

  const handleMapClick = (event) => {
    if (isAddingPin) {
      const newMarker = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    }
  };
  
  // Delete marker by index
  const deleteMarker = (index) => {
    setMarkers((prevMarkers) => prevMarkers.filter((_, i) => i !== index));
  };

  // Toggle adding pin mode
  const toggleAddPin = () => {
    setIsAddingPin(!isAddingPin);
    setIsDeletingPin(false);
  };
  
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Toggle deleting pin mode
  const toggleDeletePin = () => {
    setIsDeletingPin(!isDeletingPin);
    setIsAddingPin(false);
  };

  const handleSaveDesign = async (e) => {
    e.preventDefault();
  
    if (geofence_name.trim()) {
      console.log('Saving design with name:', geofence_name);
      console.log('Markers:', markers);

    if (!markers || markers.length < 3) {
      alert('A geofence requires at least 3 markers.');
      return;
    }

    if (isNaN(prescription) || prescription < 0 || prescription > 100) {
      alert('Please enter a valid prescription value in inches.');
      return;
    }

    console.log('Saving design with name:', geofence_name);
    console.log('Markers:', markers);
    console.log('Prescription:', prescription);
  
      try {
        const dataToSend = {
          geofence_name: geofence_name,
          points: markers.map(marker => [marker.lat, marker.lng]),
          prescription: Number(prescription),
          system_id: selectedSystem.id,
        };
  
        console.log('Payload being sent:', dataToSend);
  
        const token = localStorage.getItem('access');
  
        if (!token) {
          console.error('No token found');
          return;
        }
  
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/geofences/`,
          dataToSend,
          {
            headers: {
              Authorization: `JWT ${token}`,
              'Content-Type': 'application/json',
            },
            withCredentials: true,
          }
        );
  
        console.log('Geofence saved successfully:', response.data);
        setIsSuccessModalOpen(true);
        setIsModalOpen(false);
      } catch (error) {
        console.error('Error saving geofence:', error.response?.data || error.message);
        alert('Failed to save the geofence. Please try again.');
      }
    } else {
      alert('Please enter a name for the geofence');
    }
  };

  const loadGeofenceDesign = (geofence) => {
    if (!geofence) return;
  
    const updatedMarkers = geofence.points.map(([lat, lng]) => ({ lat, lng }));
    setMarkers(updatedMarkers);
    setPrescription(geofence.prescription);
    setIsAreaGenerated(true);
    console.log("Loaded geofence design:", geofence);
  };
  
  const handleDeleteGeofence = async (geofenceId) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this geofence?"
    );

    if (!confirmation) return;

    try {
      const token = localStorage.getItem("access");
      if (!token) {
        console.error("No token found");
        return;
      }
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/geofences/${geofenceId}/`,
        {
          headers: {
            Authorization: `JWT ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      alert("Geofence deleted successfully.");
      setGeofences((prev) => prev.filter((geofence) => geofence.id !== geofenceId));
      setIsDeleteModalOpen(false);
      setMarkers([]);
      setIsAreaGenerated(false);
      setgeofence_name('');
      setIsAddingPin(false);
      setIsDeletingPin(false);
      setIsMapLocked(false);

    } catch (error) {
      console.error("Error deleting geofence:", error.response?.data || error.message);
      alert("Failed to delete the geofence. Please try again.");
    }
  };

  const openDeleteModal = async () => {
    try {
      const token = localStorage.getItem("access");
      if (!token) {
        console.error("No token found");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/geofences/`,
        {
          headers: {
            Authorization: `JWT ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      setGeofences(response.data.geofences);
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error("Error loading geofences:", error.response?.data || error.message);
      alert("Failed to load saved designs. Please try again.");
    }
  };

  const handleOnLoad = (map) => {
    map.setMapTypeId('satellite');
  };

  const clearMap = () => {
    const confirmation = window.confirm(
      'Do you want to start a new design? This will clear your current progress.'
    );

    if (confirmation) {
      setMarkers([]);
      setIsAreaGenerated(false);
      setgeofence_name('');
      setIsAddingPin(false);
      setIsDeletingPin(false);
      setIsMapLocked(false);
      setPrescription(false);
      setSelectedGeofence(null);
    }
  };
  
  const toggleMapLock = () => {
    setIsMapLocked(!isMapLocked);
  };

  const toggleAreaGeneration = () => {
    setIsAreaGenerated(!isAreaGenerated);
  };

  // Function to handle geolocation button click
  const handleGeolocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition( // Updates as user moves
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Update user location and map center
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter({ lat: latitude, lng: longitude });
  
          if (mapRef.current) {
            mapRef.current.panTo({ lat: latitude, lng: longitude });
          }
  
          // Clear existing geofence and reset selections
          setMarkers([]);
          setIsAreaGenerated(false);
          setgeofence_name('');
          setIsAddingPin(false);
          setIsDeletingPin(false);
          setIsMapLocked(false);
          setPrescription(false);
          setSelectedGeofence(null); // Reset geofence dropdown
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          alert("Could not retrieve your location.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };  
  
  const defaultCenter = { lat: 54.5238, lng: -110.0597 };

  const center = calculateCenter(markers);

  if (!isScriptLoaded) {
    return <div>Loading map...</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        flexDirection: 'column',
      }}
    >
      {/* Map Section */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <GoogleMap
          id="my-map"
          mapContainerStyle={{
            height: '500px',
            width: '500px',
            borderRadius: '10px',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          }}
          zoom={selectedSystem ? 15.5 : 2}
          center={mapCenter} 
          mapTypeId={mapType}
          onLoad={handleOnLoad}
          options={{
            draggable: !isMapLocked,
            scrollwheel: !isMapLocked,
            disableDoubleClickZoom: isMapLocked,
            gestureHandling: isMapLocked ? 'none' : 'auto',
            tilt: 0,
          }}
          onClick={handleMapClick}
        >
        {/* Pegman Marker for User's Location */}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={{
              url: pegman,
              scaledSize: new window.google.maps.Size(25, 40),
              anchor: new window.google.maps.Point(12, 40), 
            }}
          />
        )}
          {markers.map((marker, index) => (
            <Marker
              key={index}
              position={marker}
              onClick={() => {
                if (isDeletingPin) {
                  deleteMarker(index);
                }
              }}
              icon={{
                path: 'M 0,0 m -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0',
                fillColor: 'red',
                fillOpacity: 1,
                scale: 2,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
            />
          ))}
          
          {isAreaGenerated && markers.length >= 3 && (
            <Polygon
              paths={markers}
              options={{
                fillColor: 'green',
                fillOpacity: 0.3,
                strokeColor: 'green',
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
            />
          )}
        {/* Prescription Label as Overlay */}
        <OverlayView
          position={center}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              padding: '5px 35px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 'bold',
              color: 'black',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {prescription} inches
          </div>
        </OverlayView>
        </GoogleMap>
      </div>
        {/* Systems Section */}
          <select
            id="systems-dropdown"
            style={{
              fontSize: "16px",
              padding: "10px",
              width: "250px",
              cursor: "pointer",
              border: "1px solid #ccc",
              marginBottom: "10px",
              marginTop: "10px",
              borderRadius: '5px'
            }}
            onChange={(e) => {
              const selected = systems.find(
                (system) => system.id === parseInt(e.target.value)
              );
              setSelectedSystem(selected);
              fetchGeofencesForSystem(selected.id);
            }}
          >
            <option value="" disabled selected>
              Select a system
            </option>
            {systems.map((system) => (
              <option key={system.id} value={system.id}>
                {system.thing_name}
              </option>
            ))}
          </select>
      {/* Geofences Section */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {loading ? (
          <p style={{ textAlign: 'center', color: 'gray', marginBottom: '20px' }}>
            Loading geofences...
          </p>
        ) : (
          <select
            id="geofences-dropdown"
            style={{
              fontSize: "16px",
              padding: "10px",
              width: "250px",
              cursor: "pointer",
              border: "1px solid #ccc",
              marginBottom: "20px",
              borderRadius: '5px'
            }}
            value={selectedGeofence || ""} // Reset dropdown on clear
            onChange={(e) => {
              const selected = geofences.find((geofence) => geofence.id === parseInt(e.target.value));
              setSelectedGeofence(selected?.id || ""); // Update selected geofence state
              loadGeofenceDesign(selected);
            }}
            disabled={geofences.length === 0}
          >
            <option value="" disabled>
              Select a geofence
            </option>
            {geofences.map((geofence) => (
              <option key={geofence.id} value={geofence.id}>
                {geofence.geofence_name}
              </option>
            ))}
          </select>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Button Row 1 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              width: '100%',
              marginTop: '-20px'
            }}
          >
            <button
              onClick={toggleAddPin}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: "#1e90ff",
                color: 'white',
                borderRadius: '5px'
              }}
            >
              {isAddingPin ? 'Stop Adding Pins' : 'Add Pin'}
            </button>

            <button
              onClick={toggleDeletePin}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: '#dc143c',
                color: 'white',
                borderRadius: '5px'
              }}
            >
              {isDeletingPin ? 'Cancel Remove Pins' : 'Remove Pin'}
            </button>
          </div>

          {/* Button Row 2 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row', 
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <button
              onClick={toggleMapLock}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: "#696969",
                color: 'white',
                borderRadius: '5px'
              }}
            >
              {isMapLocked ? 'Release Map' : 'Lock Map'}
            </button>
            <button
              onClick={toggleAreaGeneration}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: "#228b22",
                color: 'white',
                borderRadius: '5px'
              }}
            >
              {isAreaGenerated ? 'Remove Area' : 'Generate Area'}
            </button>
          </div>

          {/* Button Row 3 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              width: '100%',
            }}
          >
          </div>

          {/* Button Row 4 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <button
              onClick={clearMap}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: '#4682b4',
                color: 'white',
                borderRadius: '5px'
              }}
            >
              Clear / New Design
            </button>

            <button
              onClick={openDeleteModal}
              style={{
                margin: '10px',
                padding: '10px',
                width: '200px',
                backgroundColor: '#800000',
                color: 'white',
                borderRadius: '5px'
              }}
            >
              Delete Design
            </button>
          </div>
        </div>
          <button
            onClick={openModal}
            style={{
              margin: '10px',
              padding: '10px',
              width: '200px',
              backgroundColor: '#3cb371',
              color: 'white',
              borderRadius: '5px'
            }}
          >
            Save & Set Prescription
          </button>
          <button
          onClick={handleGeolocationClick}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          Use My Location
        </button>
      </div>
      {isDeleteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "300px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              position: "relative",
            }}
          >
            <h4>Delete Designs</h4>
            {geofences.length > 0 ? (
              <ul>
                {geofences.map((geofence) => (
                  <li key={geofence.id}>
                    <button
                      onClick={() => handleDeleteGeofence(geofence.id)}
                      style={{
                        padding: "10px",
                        backgroundColor: "#800000",
                        color: "white",
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "16px",
                        margin: "5px 0",
                      }}
                    >
                      {geofence.geofence_name} - {geofence.prescription || 'N/A'}%
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No saved designs available.</p>
            )}
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              style={{
                padding: "10px",
                backgroundColor: "red",
                color: "white",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
                position: "absolute",
                top: "10px",
                right: "10px",
              }}
            >
              X
            </button>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '300px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
          <h4>Save Design</h4>
            <input
              type="text"
              value={geofence_name}
              onChange={(e) => setgeofence_name(e.target.value)}
              placeholder="Enter a name for your Geofence"
              style={{
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ccc',
                fontSize: '16px',
              }}
            />
            <input
              type="number"
              value={prescription}
              onChange={(e) => setPrescription(e.target.value)}
              placeholder="Enter a prescription in inches"
              style={{
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ccc',
                fontSize: '16px',
                marginBottom: '10px',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={handleSaveDesign}
                style={{
                  padding: '10px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Save
              </button>
              <button
                onClick={closeModal}
                style={{
                  padding: '10px',
                  backgroundColor: '#b22222',
                  color: 'white',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            textAlign: "center",
            zIndex: 1000,
          }}
        >
          <h3 style={{ color: "green" }}>Geofence Saved Successfully!</h3>
          <button
            onClick={() => setIsSuccessModalOpen(false)}
            style={{
              padding: "10px",
              backgroundColor: "#4CAF50",
              color: "white",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "16px",
              marginTop: "10px",
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default Geofence;
